/**
 * Button Component
 * Reusable button with loading state and variants
 */

import React from 'react';

/**
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Button content
 * @param {boolean} props.loading - Loading state
 * @param {boolean} props.disabled - Disabled state
 * @param {string} props.variant - Button variant ('primary', 'secondary', 'outline')
 * @param {string} props.size - Button size ('sm', 'md', 'lg')
 * @param {Function} props.onClick - Click handler
 * @param {string} props.type - Button type
 * @param {string} props.className - Additional CSS classes
 * @param {React.ReactNode} props.icon - Icon component
 * @param {React.ReactNode} props.loadingIcon - Loading icon component
 */
const Button = ({
  children,
  loading = false,
  disabled = false,
  variant = 'primary',
  size = 'md',
  onClick,
  type = 'button',
  className = '',
  icon,
  loadingIcon,
  ...rest
}) => {
  const baseClasses = 'group relative flex justify-center items-center border border-transparent font-semibold rounded-xl focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transform transition-all duration-200 active:scale-[0.98]';

  const variantClasses = {
    primary: 'text-white bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 hover:scale-[1.02] hover:shadow-xl',
    indigo: 'text-white bg-indigo-600 hover:bg-indigo-700 hover:scale-[1.02] hover:shadow-md',
    secondary: 'text-gray-700 bg-white border-2 border-gray-200 hover:bg-gray-50 hover:border-gray-300 hover:scale-[1.02]',
    outline: 'text-indigo-600 bg-transparent border-2 border-indigo-600 hover:bg-indigo-50 hover:scale-[1.02]',
    success: 'text-white bg-gradient-to-r from-green-500 via-emerald-500 to-teal-500 hover:from-green-600 hover:via-emerald-600 hover:to-teal-600 hover:scale-105 hover:shadow-xl shadow-green-200/50',
    inverse: 'text-indigo-600 bg-white hover:bg-indigo-50 hover:scale-[1.02]',
    orange: 'text-white bg-orange-500 hover:bg-orange-600 hover:scale-[1.02] shadow-sm hover:shadow-md transition-all duration-200',
    danger: 'text-white bg-red-600 hover:bg-red-700 hover:scale-[1.02] shadow-sm hover:shadow-md',
  };

  const sizeClasses = {
    sm: 'py-2 px-3 text-sm',
    md: 'py-3 px-4 text-sm',
    lg: 'py-4 px-8 text-base',
  };

  const defaultLoadingIcon = (
    <svg className="h-5 w-5 text-white animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
    </svg>
  );

  const displayIcon = loading ? (loadingIcon || defaultLoadingIcon) : icon;

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled || loading}
      className={`${baseClasses} ${variantClasses[variant]} ${sizeClasses[size]} ${className}`}
      {...rest}
    >
      {displayIcon && (
        <span className="absolute left-0 inset-y-0 flex items-center pl-3">
          {displayIcon}
        </span>
      )}
      {children}
    </button>
  );
};

export default Button;
